import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-923477c6"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "white-keys" }
const _hoisted_3 = ["aria-label", "aria-selected", "onClick", "onFocus"]
const _hoisted_4 = { class: "black-keys" }
const _hoisted_5 = ["aria-label", "aria-selected", "onClick", "onFocus"]

import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from "vue";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";
import { sharpNoteNames } from "@/utils/noteUtils";
import NoteLabel from "./NoteLabel.vue";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";


export default /*#__PURE__*/_defineComponent({
  __name: 'KeySelector',
  emits: ["selector-opened", "selector-closed"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const store = useFretboardStore();
const { rootNote, accidentalDisplayMode } = storeToRefs(store);

// Global tooltip coordination
const { hideTooltip: hideGlobalTooltip, hideAllTooltips } = useGlobalTooltip();

const isOpen = ref(false);
const focusedKeyIndex = ref(0);
const selectorRef = ref<HTMLElement>();

const currentRootNote = computed(() => rootNote.value);
const currentRootIndex = computed(() =>
  sharpNoteNames.indexOf(currentRootNote.value)
);

// Piano key layout data based on Figma design
const whiteKeys = computed(() => [
  { name: "C", chromaticIndex: 0, position: 8 },
  { name: "D", chromaticIndex: 2, position: 52 },
  { name: "E", chromaticIndex: 4, position: 96 },
  { name: "F", chromaticIndex: 5, position: 140 },
  { name: "G", chromaticIndex: 7, position: 184 },
  { name: "A", chromaticIndex: 9, position: 228 },
  { name: "B", chromaticIndex: 11, position: 272 },
]);

const blackKeys = computed(() => [
  { name: "C#", chromaticIndex: 1, position: 32 }, // Between C and D
  { name: "D#", chromaticIndex: 3, position: 76 }, // Between D and E
  { name: "F#", chromaticIndex: 6, position: 162 }, // Between F and G
  { name: "G#", chromaticIndex: 8, position: 206 }, // Between G and A
  { name: "A#", chromaticIndex: 10, position: 250 }, // Between A and B
]);

const allKeys = computed(() =>
  [...whiteKeys.value, ...blackKeys.value].sort(
    (a, b) => a.chromaticIndex - b.chromaticIndex
  )
);

function openSelector() {
  console.log("openSelector called"); // Debug log
  isOpen.value = true;
  focusedKeyIndex.value = currentRootIndex.value;

  // Kill ALL tooltips globally when piano opens
  hideAllTooltips();

  // Emit event to parent
  emit("selector-opened");

  // Focus the selected key after opening
  nextTick(() => {
    const selectedKey = selectorRef.value?.querySelector(
      ".piano-key--selected"
    ) as HTMLElement;
    if (selectedKey) {
      selectedKey.focus();
    }
  });
}

function closeSelector() {
  isOpen.value = false;
  focusedKeyIndex.value = 0;

  // Emit event to parent
  emit("selector-closed");
}

function selectKey(noteName: string) {
  store.setRootNote(noteName);
  closeSelector();
}

function handleTriggerKeydown(event: KeyboardEvent) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    openSelector();
  }
}

function handleKeyboardNavigation(event: KeyboardEvent) {
  switch (event.key) {
    case "Escape":
      event.preventDefault();
      closeSelector();
      break;

    case "Enter":
    case " ": {
      event.preventDefault();
      const focusedKey = allKeys.value.find(
        (key) => key.chromaticIndex === focusedKeyIndex.value
      );
      if (focusedKey) {
        selectKey(focusedKey.name);
      }
      break;
    }

    case "ArrowLeft":
      event.preventDefault();
      moveFocus(-1);
      break;

    case "ArrowRight":
      event.preventDefault();
      moveFocus(1);
      break;

    case "Home":
      event.preventDefault();
      focusedKeyIndex.value = 0;
      focusKeyAtIndex(0);
      break;

    case "End":
      event.preventDefault();
      focusedKeyIndex.value = 11;
      focusKeyAtIndex(11);
      break;
  }
}

function moveFocus(direction: number) {
  const currentIndex = allKeys.value.findIndex(
    (key) => key.chromaticIndex === focusedKeyIndex.value
  );
  const newIndex = Math.max(
    0,
    Math.min(allKeys.value.length - 1, currentIndex + direction)
  );
  const newKey = allKeys.value[newIndex];

  if (newKey) {
    focusedKeyIndex.value = newKey.chromaticIndex;
    focusKeyAtIndex(newKey.chromaticIndex);
  }
}

function focusKeyAtIndex(chromaticIndex: number) {
  nextTick(() => {
    const keyButton = selectorRef.value?.querySelector(
      `[aria-label*="${sharpNoteNames[chromaticIndex]}"]`
    ) as HTMLElement;
    if (keyButton) {
      keyButton.focus();
    }
  });
}

function handleBlur() {
  // Only close if focus is moving outside the entire selector
  setTimeout(() => {
    if (
      selectorRef.value &&
      !selectorRef.value.contains(document.activeElement)
    ) {
      closeSelector();
    }
  }, 0);
}

function handleClickOutside(event: MouseEvent) {
  if (
    isOpen.value &&
    selectorRef.value &&
    !selectorRef.value.contains(event.target as Node)
  ) {
    closeSelector();
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

// Watch for root note changes from external sources
watch(rootNote, () => {
  focusedKeyIndex.value = currentRootIndex.value;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["key-selector", { 'key-selector--open': isOpen.value }]),
    ref_key: "selectorRef",
    ref: selectorRef
  }, [
    (!isOpen.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          class: "root-selector-trigger interactive-outline",
          "aria-label": `Root note: ${currentRootNote.value}. Click to open key selector`,
          onClick: _withModifiers(openSelector, ["stop"]),
          onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
          onKeydown: handleTriggerKeydown
        }, [
          _createVNode(NoteLabel, {
            "chromatic-index": currentRootIndex.value,
            "display-mode": _unref(accidentalDisplayMode),
            "key-signature": currentRootNote.value,
            size: "medium"
          }, null, 8, ["chromatic-index", "display-mode", "key-signature"])
        ], 40, _hoisted_1))
      : _createCommentVNode("", true),
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "root-selector-open",
          role: "listbox",
          "aria-label": 'Select root note',
          onKeydown: handleKeyboardNavigation
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_TransitionGroup, {
              name: "piano-white-key",
              appear: ""
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(whiteKeys.value, (note, index) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: `white-${note.name}`,
                    class: _normalizeClass(["piano-key piano-key--white", {
              'piano-key--selected': note.name === currentRootNote.value,
              'piano-key--focused': focusedKeyIndex.value === note.chromaticIndex,
            }]),
                    style: _normalizeStyle({
              left: `${note.position}px`,
              '--animation-delay': `${index * 20}ms`,
            }),
                    "aria-label": `${note.name} key`,
                    "aria-selected": note.name === currentRootNote.value,
                    role: "option",
                    onClick: ($event: any) => (selectKey(note.name)),
                    onFocus: ($event: any) => (focusedKeyIndex.value = note.chromaticIndex),
                    onBlur: handleBlur
                  }, [
                    _createVNode(NoteLabel, {
                      "chromatic-index": note.chromaticIndex,
                      "display-mode": _unref(accidentalDisplayMode),
                      "key-signature": currentRootNote.value,
                      size: "medium"
                    }, null, 8, ["chromatic-index", "display-mode", "key-signature"])
                  ], 46, _hoisted_3))
                }), 128))
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_TransitionGroup, {
              name: "piano-black-key",
              appear: ""
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(blackKeys.value, (note, index) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: `black-${note.name}`,
                    class: _normalizeClass(["piano-key piano-key--black", {
              'piano-key--selected': note.name === currentRootNote.value,
              'piano-key--focused': focusedKeyIndex.value === note.chromaticIndex,
            }]),
                    style: _normalizeStyle({
              left: `${note.position}px`,
              '--animation-delay': `${(index + whiteKeys.value.length) * 10}ms`,
            }),
                    "aria-label": `${note.name} key`,
                    "aria-selected": note.name === currentRootNote.value,
                    role: "option",
                    onClick: ($event: any) => (selectKey(note.name)),
                    onFocus: ($event: any) => (focusedKeyIndex.value = note.chromaticIndex),
                    onBlur: handleBlur
                  }, [
                    _createVNode(NoteLabel, {
                      "chromatic-index": note.chromaticIndex,
                      "display-mode": _unref(accidentalDisplayMode),
                      "key-signature": currentRootNote.value,
                      size: "small"
                    }, null, 8, ["chromatic-index", "display-mode", "key-signature"])
                  ], 46, _hoisted_5))
                }), 128))
              ]),
              _: 1
            })
          ])
        ], 32))
      : _createCommentVNode("", true)
  ], 2))
}
}

})